import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import SortColumnIcon from 'teacher-ui-client/components/sort-column-icon'
import { t } from 'ember-intl'
import { eq } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import AssignmentStatus from 'teacher-ui-client/components/assignments-admin/assignment-status'
import type Assignment from 'teacher-ui-client/models/assignment'
import { service } from '@ember/service'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type Store from '@ember-data/store'
import { colspanMax } from 'teacher-ui-client/modifiers/colspan-max'
import type Student from 'teacher-ui-client/models/student'
import type Owner from '@ember/owner'
import type { AssignmentScoreDataItem } from 'teacher-ui-client/utils/student-events/fetchers/assignment-scores'

interface AssignedStudentsViewModel {
  studentId: string
  status: string
  schoolClassNames: string
  score: Nullable<number>
  attempts: Nullable<number>
  student?: Student
}

interface Signature {
  Element: HTMLDivElement
  Args: {
    assignment: Assignment
    scoresForAssignment: AssignmentScoreDataItem['students']
  }
}

/**
 * Table of students for a particular assignment
 */
export class InnerStudentScoresTable extends Component<Signature> {
  @service store!: Store

  @service subscriptionType!: SubscriptionTypeService

  @tracked currentSortColumn?: string

  @tracked sortingPriorityQueue = ['firstName', 'lastName', 'gradePosition'].map((columnProp) => ({
    key: columnProp,
    sortDirection: 'asc' as const,
  }))

  processor: DataProcessor<AssignedStudentsViewModel>

  get data() {
    return this.assignedStudents
  }

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get assignedStudents(): AssignedStudentsViewModel[] {
    const { assignment } = this.args
    if (!assignment?.assignedStudents) return []

    const students = this.store.peekAll<Student>('student')
    const subscriptionType = this.subscriptionType.current

    // Match assignment students to their data & scores
    return assignment.assignedStudents.map((assignedStudent) => {
      const studentScoreItem = this.args.scoresForAssignment.find(
        (scoreItem) => String(scoreItem.student_id) === String(assignedStudent.canonical_student_id),
      )

      const score = studentScoreItem?.average_score ?? null
      const attempts = studentScoreItem?.total_attempts ?? null

      const student = students.find((s) => s.id === String(assignedStudent.canonical_student_id))
      const schoolClassNames =
        student?.schoolClasses
          ?.filter((sc) => sc.subscriptionType === subscriptionType)
          .map((sc) => sc.name)
          .join(', ') ?? ''

      const { canonical_student_id, status } = assignedStudent

      return {
        studentId: String(canonical_student_id),
        status,
        student,
        schoolClassNames,
        score,
        attempts,
      }
    })
  }

  <template>
    <div class="w-full overflow-auto" data-test-inner-student-scores-table ...attributes>
      {{#let this.processor.transformedData this.processor.tableAction as |transformedData|}}
        <table class="w-full table-fixed divide-y divide-white">
          <thead>
            <tr class="divide-x divide-white">
              <th
                class="bg-juicy-orange-100 min-w-fit p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "student.firstName")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.firstName"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="student.firstName"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 min-w-fit p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "student.lastName")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.lastName"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="student.lastName"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "schoolClassNames")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.classes"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="schoolClassNames"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 w-24 p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "student.gradePosition")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "grade.label"}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="student.gradePosition"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 w-40 p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "status")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.studentStatus"}}
                  </span>
                  <SortColumnIcon @sortingPriorityQueue={{this.processor.sortingPriorityQueue}} @columnName="status" />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 w-32 p-3"
                {{on "click" (fn this.processor.tableActions.sortByNumber "attempts")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{#if (eq @assignment.assignmentType "chapter")}}
                      {{t "assignments.manage.chapterAttemptsLabel"}}
                    {{else}}
                      {{t "assignments.manage.attemptsLabel"}}
                    {{/if}}
                  </span>
                  <SortColumnIcon
                    @sortingPriorityQueue={{this.processor.sortingPriorityQueue}}
                    @columnName="attempts"
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 w-36 p-3"
                {{on "click" (fn this.processor.tableActions.sortByNumber "score")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.averageScoreAsPercentage" htmlSafe=true}}
                  </span>
                  <SortColumnIcon @sortingPriorityQueue={{this.processor.sortingPriorityQueue}} @columnName="score" />
                  <span class="ml-auto print:hidden">
                    <PhosphorIcon @icon="phosphor-info-fill" class="h-4 w-4 cursor-pointer" role="presentation" />
                    <Tooltip @text={{t "assignments.studentsOfAssignmentAverageScoreTooltip"}} role="presentation" />
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-white">
            {{#each transformedData as |row|}}
              <tr data-test-inner-student-scores-table-row={{row.studentId}}>
                {{#if row.student}}
                  <td data-test-first-name class="bg-juicy-orange-50 p-3">
                    {{row.student.firstName}}
                  </td>
                  <td class="bg-juicy-orange-50 p-3 text-left">
                    {{row.student.lastName}}
                  </td>
                {{else}}
                  <td class="bg-juicy-orange-50 p-3 text-left">
                    {{t "assignments.assignedStudentNotFound"}}
                  </td>
                  <td class="bg-juicy-orange-50 p-3 text-left"></td>
                {{/if}}
                <td class="bg-juicy-orange-50 p-3 text-left">
                  {{row.schoolClassNames}}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left">
                  {{formatValue (gradeName row.student.gradePosition)}}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left">
                  <AssignmentStatus @status={{row.status}} class="float-left" />
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left" data-test-attempts>
                  {{formatValue row.attempts}}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left" data-test-score>
                  {{formatValue row.score append="%"}}
                </td>
              </tr>
            {{else}}
              <tr>
                <td class="bg-juicy-orange-50 p-3" {{colspanMax}}>
                  <div class="missing-students-data">
                    {{t "assignments.missingStudentsDataInList"}}
                  </div>
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      {{/let}}
    </div>
  </template>
}
